/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import { addDays } from 'date-fns'
import { formatDate } from '../../components/Calendar/DateFormatter/DateFormatter'
import drawHeader from '../../drawing/drawHeader'
import Tooltip from '../../components/Price/Tooltip'
import selectColumn from '../../actions/bookingCalendar/calendar/selectColumn'
import { isTouchDevice } from '../../components/MediaQuery'
import { useIsMobile } from '../../components/MediaQuery/Mobile'
import { CountryCode } from '../../i18n/countryCodes'
import { Mixpanel } from '../../utils/Mixpanel'
import { PopoveredIcon2 } from '../../components/PopoveredIcon/PopoveredIcon'
import { isLicenseValid } from '../../reducers/userAccount/selectors/license'
import UpgradePremiumBlockScreenModal from '../UpgradePremiumBlockScreenModal/UpgradePremiumBlockScreenModal'
import { getUserPriceGroup } from '../../reducers/userAccount/selectors/userAccount'

const HeaderCanvas = ({
  startDay,
  scrollLeft,
  width,
  height,
  selectedDays,
  constants,
  dpConstants,
  size,
  style,
  markDay,
  refHeight,
  preview = false,
  minDate = null,
}) => {
  const isMobile = useIsMobile()
  const [canvas, setCanvas] = useState(null)
  const bufferCanvas = useMemo(() => document.createElement('canvas'), [])
  const [t, i18n] = useTranslation()
  const [hoveredDayX, setHoveredDayX] = useState(null)
  const [wrapperRef, setWrapperRef] = useState(null)
  const [wrapperRect, setWrapperRect] = useState(null)
  const [canvasWidth, setCanvasWidth] = useState(
    width * dpConstants.PIXEL_RATIO,
  )
  useEffect(() => setCanvasWidth(width * dpConstants.PIXEL_RATIO), [
    width,
    dpConstants,
  ])
  const [canvasHeight, setCanvasHeight] = useState(
    width * dpConstants.PIXEL_RATIO,
  )
  useEffect(() => setCanvasHeight(height * dpConstants.PIXEL_RATIO), [
    height,
    dpConstants,
  ])

  const formatHeaderDay = useCallback(
    day => formatDate(day, 'dd', i18n.language as CountryCode),
    [i18n],
  )

  useEffect(() => {
    bufferCanvas.height = canvasHeight
    bufferCanvas.width = canvasWidth + dpConstants.GRID_WIDTH
    if (canvas) {
      canvas.height = canvasHeight
      canvas.width = canvasWidth
    }
    if (wrapperRef) {
      setWrapperRect(wrapperRef.getBoundingClientRect())
    }
  }, [wrapperRef, canvasWidth, dpConstants, refHeight])

  useEffect(() => {
    const ctx = bufferCanvas.getContext('2d')
    ctx.clearRect(0, 0, bufferCanvas.width, bufferCanvas.height)

    drawHeader(
      ctx,
      dpConstants,
      bufferCanvas.width,
      startDay,
      0,
      selectedDays,
      formatHeaderDay,
    )
  }, [
    canvasWidth,
    canvasHeight,
    +startDay,
    i18n.language,
    selectedDays,
    dpConstants,
    refHeight,
  ])

  useEffect(() => {
    if (canvas) {
      const xOffset =
        Math.round(scrollLeft % constants.GRID_WIDTH) * dpConstants.PIXEL_RATIO
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(
        bufferCanvas,
        xOffset,
        0,
        canvas.width,
        canvas.height,
        0,
        0,
        canvas.width,
        canvas.height,
      )
    }
  }, [
    canvasWidth,
    canvasHeight,
    selectedDays,
    dpConstants,
    i18n.language,
    +minDate,
    scrollLeft,
    refHeight,
  ])

  const dayTooltipRefObject = useCallback(() => {
    if (!wrapperRect) {
      return {}
    }

    return {
      clientHeight: constants.HEADER_HEIGHT,
      clientWidth: constants.GRID_WIDTH,
      getBoundingClientRect: () => ({
        bottom: wrapperRect.top + constants.HEADER_HEIGHT,
        height: constants.HEADER_HEIGHT,
        left: wrapperRect.left + hoveredDayX,
        right: wrapperRect.left + hoveredDayX + constants.GRID_WIDTH,
        top: wrapperRect.top,
        width: constants.GRID_WIDTH,
      }),
    }
  }, [constants, hoveredDayX, wrapperRect])

  const mouseMoveListener = e => {
    if (wrapperRect) {
      const x = e.clientX - wrapperRect.left
      const xOffset = Math.round(scrollLeft % constants.GRID_WIDTH)

      setHoveredDayX(
        Math.floor((x + xOffset) / constants.GRID_WIDTH) *
          constants.GRID_WIDTH -
          xOffset,
      )
    }
  }

  const clickListener = e => {
    const x = e.clientX - wrapperRect.left
    const xOffset = Math.round(scrollLeft % constants.GRID_WIDTH)
    const dayIndex = (x + xOffset) / constants.GRID_WIDTH
    Mixpanel.track('Mark A Day Used', {})
    markDay(addDays(startDay, dayIndex))
  }

  const mouseLeaveListener = () => {
    setHoveredDayX(null)
  }

  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const [isModalOpened, setIsModalOpened] = useState(false)
  const premium = useSelector(isLicenseValid)
  const userPriceGroup = useSelector(getUserPriceGroup)

  const handlePopoverOpen = event => {
    if (!wrapperRect) return

    const div = document.createElement('div')
    div.style.position = 'fixed'
    div.style.top = `${event.clientY}px`
    div.style.left = `${event.clientX}px`
    div.style.width = '1px'
    div.style.height = '1px'
    div.style.background = 'red'
    document.body.appendChild(div)
    event.preventDefault()
    event.stopPropagation()
    if (isMobile) {
      setIsModalOpened(!isModalOpened)
    } else {
      setPopoverAnchor(div)
    }
  }

  const handlePopoverClose = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setPopoverAnchor(null)
    setIsModalOpened(false)
  }

  return (
    <div
      style={{
        height: height,
        width: width,
        zIndex: 1,
        ...style,
      }}
      ref={setWrapperRef}
      onMouseMove={!preview && (premium && userPriceGroup !== 3) ? mouseMoveListener : undefined}
      onMouseLeave={!preview && (premium && userPriceGroup !== 3) ? mouseLeaveListener : undefined}
      onClick={premium && userPriceGroup !== 3 ? !preview ? clickListener : undefined :  handlePopoverOpen}
    >
      <canvas
        className={'HeaderCanvas'}
        ref={setCanvas}
        width={canvasWidth}
        height={canvasHeight}
        style={{
          background: '#fff',
          boxShadow: `0 1px 4px 0 rgba(0, 0, 0, 0.25)`,
          height: `${height}px`,
          position: 'absolute',
          width: `${width}px`,
        }}
      />
      {premium && <Tooltip
        placement={'bottom'}
        size={size}
        arrow={true}
        title={t('calendar_mark_day_dialog_check')}
        isRoom={true}
        fakeRef={dayTooltipRefObject}
        disableHoverListener={true}
        open={hoveredDayX !== null && !isTouchDevice && !isMobile}
      >
        <div />
      </Tooltip>}
      {!premium && <PopoveredIcon2 anchorEl={popoverAnchor} onClose={handlePopoverClose}/>}
      {isMobile && <UpgradePremiumBlockScreenModal
        open={isModalOpened}
        close={() => setIsModalOpened(false)}
      />}
    </div>
  )
}

const mapDispatchToProps = dispatch => ({
  markDay: day => dispatch(selectColumn(day)),
})

export default connect(
  null,
  mapDispatchToProps,
)(HeaderCanvas)
